import React from 'react';
import { Activity, ArrowRight } from 'lucide-react';

const parseApiDate = (s) => {
  if (!s) return null;
  const parts = s.split('-');
  if (parts.length === 3 && isNaN(parseInt(parts[1]))) {
    const m = { Jan:0,Feb:1,Mar:2,Apr:3,May:4,Jun:5,Jul:6,Aug:7,Sep:8,Oct:9,Nov:10,Dec:11 };
    return new Date(parseInt(parts[2]), m[parts[1]] ?? 0, parseInt(parts[0]));
  }
  return new Date(s);
};

const calcProgress = (startStr, endStr) => {
  const s = parseApiDate(startStr), e = parseApiDate(endStr);
  if (!s || !e || isNaN(s) || isNaN(e)) return 0;
  const total = e - s;
  return Math.min(Math.max(((new Date() - s) / total) * 100, 0), 100);
};

const fmt = (dateStr) => {
  const d = parseApiDate(dateStr);
  if (!d || isNaN(d)) return '';
  return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
};

// "17 Jun 2035" (BIRTH-TIME-1 "Dasha at birth" card).
const fmtDay = (dateStr) => {
  const d = parseApiDate(dateStr);
  if (!d || isNaN(d)) return '';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

const label = { fontFamily: 'Montserrat, sans-serif', fontSize: 9, fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.12em' };

// Owner ruling 19 Oct 2026: a person not born yet sees the dasha running AT birth — no
// antardasha bar, no "today" position. Every date comes from the engine response.
const DashaAtBirth = ({ state, onNavigate, subjectName }) => (
  <div className="jp-card" data-testid="dasha-timeline-card" data-state="future-birth">
    <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 12 }}>
      <Activity style={{ width: 13, height: 13, color: 'var(--gold-primary)', flexShrink: 0 }} />
      <span style={label} data-testid="dasha-at-birth-title">Dasha at birth{subjectName ? ` · ${subjectName}` : ''}</span>
    </div>
    <p style={{ fontFamily: 'Cinzel, serif', fontSize: 15, color: 'var(--gold-bright)', fontWeight: 500, marginBottom: 12 }}
      data-testid="dasha-at-birth-md">
      {state.mahadasha.planet} Mahadasha · until {fmtDay(state.mahadasha.end_date)}
    </p>
    {state.next.length > 0 && (
      <div style={{ paddingTop: 10, borderTop: '1px solid var(--card-border-subtle)' }}>
        <p style={{ ...label, fontWeight: 400, letterSpacing: '0.1em', marginBottom: 8 }}>Then</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          {state.next.map((md, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between' }} data-testid="dasha-at-birth-next">
              <span style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 10, color: 'var(--text-secondary)' }}>{md.planet} Mahadasha</span>
              <span style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 9, color: 'var(--text-tertiary)' }}>from {fmtDay(md.start_date)}</span>
            </div>
          ))}
        </div>
      </div>
    )}
    <button onClick={() => onNavigate('dasha')} data-testid="view-full-timeline-btn"
      style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 12, background: 'none', border: 'none', cursor: 'pointer', width: '100%', justifyContent: 'flex-end' }}>
      <span style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 10, color: 'var(--text-tertiary)' }}>View full timeline</span>
      <ArrowRight style={{ width: 11, height: 11, color: 'var(--text-tertiary)' }} />
    </button>
  </div>
);

const BirthTimeLooksWrong = ({ onNavigate }) => (
  <div className="jp-card" data-testid="dasha-timeline-card" data-state="invalid-time">
    <p style={{ fontFamily: 'Lora, serif', fontSize: 12, color: 'var(--text-secondary)' }}>
      Birth time looks wrong —{' '}
      <button onClick={() => onNavigate('profile')} data-testid="dasha-card-edit-birth-time"
        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--gold-bright)', textDecoration: 'underline', font: 'inherit' }}>
        edit
      </button>
    </p>
  </div>
);

const ProgressBar = ({ progress, endLabel }) => (
  <div style={{ marginBottom: 10 }}>
    <div style={{ height: 4, borderRadius: 2, background: 'var(--card-border)', overflow: 'hidden', marginBottom: 4 }}>
      <div style={{
        height: '100%', width: `${progress}%`,
        background: 'var(--gold-primary)', borderRadius: 2,
        transition: 'width 1s ease',
      }} />
    </div>
    <p style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 9, color: 'var(--text-tertiary)', textAlign: 'right' }}>
      ends {endLabel}
    </p>
  </div>
);

const SkeletonBlock = () => (
  <div className="cs-skeleton" style={{ height: 12, borderRadius: 4, marginBottom: 8, width: '70%' }} />
);

const DashaTimelineCard = ({ currentMahadasha, loading, onNavigate, cardState = null, subjectName = null }) => {
  if (!loading && cardState?.kind === 'future') {
    return <DashaAtBirth state={cardState} onNavigate={onNavigate} subjectName={subjectName} />;
  }
  if (!loading && cardState?.kind === 'invalid_time') {
    return <BirthTimeLooksWrong onNavigate={onNavigate} />;
  }
  if (loading) {
    return (
      <div className="jp-card" data-testid="dasha-timeline-card">
        <SkeletonBlock />
        <div className="cs-skeleton" style={{ height: 4, borderRadius: 2, width: '100%', marginBottom: 16 }} />
        <SkeletonBlock />
        <div className="cs-skeleton" style={{ height: 4, borderRadius: 2, width: '80%' }} />
      </div>
    );
  }

  if (!currentMahadasha) {
    return (
      <div className="jp-card" data-testid="dasha-timeline-card">
        <p style={{ fontFamily: 'Lora, serif', fontSize: 12, color: 'var(--text-tertiary)', fontStyle: 'italic' }}>
          Enter birth details to see your dasha timeline.
        </p>
      </div>
    );
  }

  const mdProgress = calcProgress(currentMahadasha.start_date, currentMahadasha.end_date);
  const subPeriods = currentMahadasha.sub_periods || currentMahadasha.antardashas || [];
  const now = new Date();

  const currentADIndex = subPeriods.findIndex(sp => {
    const s = parseApiDate(sp.start_date), e = parseApiDate(sp.end_date);
    return s && e && now >= s && now <= e;
  });

  const currentAD = currentADIndex >= 0 ? subPeriods[currentADIndex] : null;
  const upcomingADs = subPeriods.slice(
    currentADIndex >= 0 ? currentADIndex + 1 : 0,
    currentADIndex >= 0 ? currentADIndex + 3 : 3
  );
  const adProgress = currentAD ? calcProgress(currentAD.start_date, currentAD.end_date) : 0;

  return (
    <div className="jp-card" data-testid="dasha-timeline-card">
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 16 }}>
        <Activity style={{ width: 13, height: 13, color: 'var(--gold-primary)', flexShrink: 0 }} />
        <span style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 9, fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.12em' }}>
          CURRENT PERIOD
        </span>
      </div>

      {/* Mahadasha */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
          <span style={{ fontFamily: 'Cinzel, serif', fontSize: 16, color: 'var(--gold-bright)', fontWeight: 500 }}>
            {currentMahadasha.planet}
          </span>
          <span style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 10, color: 'var(--text-tertiary)' }}>
            Mahadasha
          </span>
        </div>
        <ProgressBar progress={mdProgress} endLabel={fmt(currentMahadasha.end_date)} />
      </div>

      {/* Antardasha */}
      {currentAD && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
            <span style={{ fontFamily: 'Cinzel, serif', fontSize: 13, color: 'var(--text-primary)', fontWeight: 500 }}>
              {currentAD.planet}
            </span>
            <span style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 10, color: 'var(--text-tertiary)' }}>
              Antardasha
            </span>
          </div>
          <ProgressBar progress={adProgress} endLabel={fmt(currentAD.end_date)} />
        </div>
      )}

      {/* Upcoming ADs */}
      {upcomingADs.length > 0 && (
        <div style={{ paddingTop: 10, borderTop: '1px solid var(--card-border-subtle)' }}>
          <p style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 9, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>
            Coming next
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            {upcomingADs.map((ad, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 10, color: 'var(--text-secondary)' }}>
                  {ad.planet} AD
                </span>
                <span style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 9, color: 'var(--text-tertiary)' }}>
                  {fmt(ad.start_date)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* View full timeline */}
      <button
        onClick={() => onNavigate('dasha')}
        style={{
          display: 'flex', alignItems: 'center', gap: 4,
          marginTop: 12, paddingTop: 10, borderTop: '1px solid var(--card-border-subtle)',
          background: 'none', border: 'none', cursor: 'pointer', width: '100%', justifyContent: 'flex-end',
        }}
        data-testid="view-full-timeline-btn"
      >
        <span style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 10, color: 'var(--text-tertiary)' }}>
          View full timeline
        </span>
        <ArrowRight style={{ width: 11, height: 11, color: 'var(--text-tertiary)' }} />
      </button>
    </div>
  );
};

export default DashaTimelineCard;
